import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import https from "https";
import axios from "axios";
import log from "../utils/log";
import metaData from "../data/metaData";
import c from "../utils/common";

//
import useAuthStore from "../states/useAuth";
import useRequestStore from "../states/useRequest";
import useEstampStore from "../states/useEstamp";
import useDB from "./useDB";

const useEstamp = () => {
  const { auth, user } = useAuthStore();
  const {
    getSingleEstamp: getSingleEstampFromCache,
    getEstamps: getEstampsFromCache,
    updateSingleEstampCache,
    updateEstampsCache,
  } = useEstampStore();

  const { get } = useDB();

  /**
   * getSingleEstamp:
   * This function fetches a single estamp by its ID, first checking if the estamp's data is cached.
   *
   * - If the estamp data is found in the cache, it returns the cached data.
   * - If the estamp data is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary object), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {number|string} id - The unique ID of the estamp to fetch.
   * @returns {Object} - The estamp data, either from the cache or fetched via API.
   */
  const getSingleEstamp = async (id, saveCache = true, useCache = true) => {
    if (!id) {
      return undefined;
    }

    let cache = useCache ? getSingleEstampFromCache(id) : null;

    if (!cache) {
      log.debug("no cache, axios");

      let response = await get("estamp/query", {
        companyId: id,
        getCurrentTwoPeriod: true,
        getCurrentGifts: true,
        populate: [
          {
            path: "shops",
            select: "id name logo",
          },
          {
            path: "giftsProducts",
            select: "-coupons -terms",
          },
        ],
      });

      // Cache the fetched estamp data
      if (saveCache) {
        updateSingleEstampCache("update", [response]);
      }
      return response; // Return the fetched data
    } else {
      log.debug("using cache");
      return cache; // Return the cached data
    }
  };

  /**
   * getEstamps:
   * This function fetches a list of estamps based on a query (such as pagination or filters),
   * first checking if the query result is cached.
   *
   * - If the query result is found in the cache, it returns the cached data.
   * - If the query result is not found in the cache, it simulates a delay (e.g., 2000ms),
   *   fetches the data (simulated here by a temporary array), stores it in the cache,
   *   and then returns the fetched data.
   *
   * @param {string|Object|undefined} query - The query used to filter or paginate the estamps.
   * @returns {Array} - The list of estamps, either from the cache or fetched via API.
   */
  const getEstamps = async (query, saveCache = true, useCache = true) => {
    let cache = useCache ? getEstampsFromCache(query) : null;
    if (cache) {
      log.debug("using cache");
      return cache; // Return cached data if available
    }

    log.debug("no cache, axios");

    // prepare body
    let body;
    if (query === undefined || typeof query === "string") {
      //transform to json
      body = {
        companyId: user?.companyId,
        getCurrentTwoPeriod: true,
        getCurrentGifts: true,
        populate: [
          {
            path: "shops",
            select: "id name logo",
          },
          {
            path: "giftsProducts",
            select: "-coupons -terms",
          },
        ],
      };
    } else if (typeof query === "object") {
      // If query is an object, assume it's already in the correct format
      body = query;
    } else {
      throw new Error("Invalid query format. It must be either undefined, a string, or an object.");
    }

    let response = await get("estamps/query", body);

    // Cache the fetched requests data
    if (typeof query !== "object" && saveCache) {
      updateEstampsCache("update", query, response); // Cache the fetched estamps data
    }
    return response; // Return the fetched data
  };

  const updateEstamp = async (companyId, data, saveCache = true) => {
    const response = await axios.patch(metaData.serverURL + "estamp", data, {
      params: { companyId: companyId }, // Pass the request ID as a query parameter if needed
      headers: { Authorization: auth.jwt },
      httpsAgent: new https.Agent({ keepAlive: true, keepAliveMsecs: 60000 }),
    });
    // log.debug(response.data);

    // Cache the fetched request data
    if (saveCache) {
      updateSingleEstampCache("update", [response.data]);
    }
    return response.data; // Return the updated data
  };

  return { getSingleEstamp, getEstamps, updateEstamp };
};

export default useEstamp;
